import { useMemo } from 'react';
import type {
  GameMode,
  RoomPhase,
  PollPayload,
  PollRuntime,
  ArenaPayload,
  ArenaRuntime,
  SequencerRuntime,
} from '@interactive-room';

export interface FlowAction {
  id: string;
  label: string;
  hint?: string;
  variant: 'primary' | 'secondary' | 'ghost';
  disabled?: boolean;
  onClick: () => void;
}

interface UseHostFlowActionsParams {
  phase: RoomPhase;
  mode: GameMode | null | undefined;
  payload: unknown;
  runtime: unknown;
  connectedCount: number;
  hasActivity: boolean;
  setPhase: (phase: RoomPhase) => Promise<void>;
  dispatchActivityAction: (action: { type: string; payload?: unknown }) => Promise<void>;
}

export function useHostFlowActions({
  phase,
  mode,
  payload,
  runtime,
  connectedCount,
  hasActivity,
  setPhase,
  dispatchActivityAction,
}: UseHostFlowActionsParams): FlowAction[] {
  return useMemo(() => {
    const go = (p: RoomPhase) => () => void setPhase(p);
    const act = (type: string, actionPayload?: unknown) => () =>
      void dispatchActivityAction({ type, payload: actionPayload });

    if (phase === 'LOBBY') {
      if (!hasActivity) {
        return [
          {
            id: 'waiting',
            label: connectedCount > 0 ? `${connectedCount} listos — elige un modo` : 'Esperando estudiantes…',
            hint: connectedCount > 0 ? 'Selecciona una actividad en el panel' : 'Comparte el código de la sala',
            variant: 'ghost',
            disabled: true,
            onClick: () => {},
          },
        ];
      }
      return [];
    }

    if (phase === 'PREPARING') {
      return [
        {
          id: 'start',
          label: '▶ Comenzar actividad',
          hint: 'Los estudiantes verán la primera pregunta',
          variant: 'primary',
          onClick: go('ACTIVE'),
        },
        {
          id: 'lobby',
          label: 'Volver al lobby',
          variant: 'ghost',
          onClick: go('LOBBY'),
        },
      ];
    }

    if (phase === 'ACTIVE' && mode === 'poll' && payload && runtime) {
      const p = payload as PollPayload;
      const r = runtime as PollRuntime;
      const isLast = r.currentSlideIndex >= p.slides.length - 1;
      return [
        {
          id: isLast ? 'results' : 'next-slide',
          label: isLast ? 'Cerrar y ver resultados' : 'Siguiente slide →',
          variant: 'primary',
          onClick: isLast ? go('RESULTS') : act('NEXT_SLIDE'),
        },
        {
          id: 'peek-results',
          label: 'Ver agregados en vivo',
          variant: 'secondary',
          onClick: go('RESULTS'),
        },
        {
          id: 'pause',
          label: 'Pausar',
          variant: 'ghost',
          onClick: go('PREPARING'),
        },
      ];
    }

    if (phase === 'ACTIVE' && mode === 'arena' && payload && runtime) {
      const p = payload as ArenaPayload;
      const r = runtime as ArenaRuntime;
      const isLast = r.currentQuestionIndex >= p.questions.length - 1;
      const revealed = r.revealed;
      return [
        !revealed
          ? {
              id: 'reveal',
              label: 'Revelar respuestas',
              variant: 'primary',
              onClick: act('REVEAL'),
            }
          : {
              id: isLast ? 'results' : 'next-q',
              label: isLast ? 'Ver resultados finales' : 'Siguiente pregunta →',
              variant: 'primary',
              onClick: isLast ? go('RESULTS') : act('NEXT_QUESTION'),
            },
        {
          id: 'results-now',
          label: 'Ver resultados',
          variant: 'secondary',
          onClick: go('RESULTS'),
        },
      ];
    }

    if (phase === 'ACTIVE') {
      return [
        {
          id: 'results',
          label: 'Ver resultados',
          variant: 'primary',
          onClick: go('RESULTS'),
        },
        {
          id: 'pause',
          label: 'Pausar',
          variant: 'ghost',
          onClick: go('PREPARING'),
        },
      ];
    }

    if (phase === 'RESULTS') {
      const actions: FlowAction[] = [];

      if (mode === 'order' && runtime && !(runtime as SequencerRuntime).revealed) {
        actions.push({
          id: 'reveal-sequence',
          label: 'Revelar secuencia correcta',
          hint: 'Muestra el orden en el proyector',
          variant: 'primary',
          onClick: act('REVEAL'),
        });
      }

      actions.push(
        {
          id: 'podium',
          label: 'Mostrar podio',
          variant: mode === 'order' && runtime && !(runtime as SequencerRuntime).revealed ? 'secondary' : 'primary',
          onClick: go('PODIUM'),
        },
        {
          id: 'resume',
          label: 'Seguir en vivo',
          variant: 'secondary',
          onClick: go('ACTIVE'),
        },
        {
          id: 'next-round',
          label: 'Nueva ronda',
          variant: 'ghost',
          onClick: go('PREPARING'),
        },
      );

      return actions;
    }

    if (phase === 'PODIUM') {
      return [
        {
          id: 'new',
          label: 'Nueva actividad',
          variant: 'primary',
          onClick: go('LOBBY'),
        },
        {
          id: 'again',
          label: 'Repetir actividad',
          variant: 'secondary',
          onClick: go('PREPARING'),
        },
      ];
    }

    if (phase === 'ENDED') {
      return [
        {
          id: 'ended',
          label: 'Sesión finalizada',
          variant: 'ghost',
          disabled: true,
          onClick: () => {},
        },
      ];
    }

    return [];
  }, [
    phase,
    mode,
    payload,
    runtime,
    connectedCount,
    hasActivity,
    setPhase,
    dispatchActivityAction,
  ]);
}
